import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import DOMPurify from "dompurify";

// --- utility function ---
export function formatRemark(rawHTML) {
  let text = rawHTML;

  // 1️⃣ Remove existing <br> tags (both lowercase and uppercase)
  text = text.replace(/<br\s*\/?>/gi, ' ');

  // 2️⃣ Remove trailing codes like "SGMKRD"
  text = text.replace(/\s*SGMKRD\s*$/i, '');

  // 3️⃣ Insert <br> before or after important keywords
  text = text
    .replace(/=\s*([A-Z ]+)\s*=/gi, '<br>$1<br>')             // Handles “=SADGURU PATTERN=”
    .replace(/\b(Karad)\s+(Date)/i, '$1<br>$2')               // After college name
    .replace(/\b(Date\s*\d{1,2}\/\d{1,2}\/\d{2,4})/i, '<br>$1') // Before "Date"
    .replace(/(Date\s*\d{1,2}\/\d{1,2}\/\d{2,4})\s*(Roll No\.?\s*\d+)/i, '$1<br>$2')
    .replace(/(Roll No\.?\s*\d+)/i, '<br>$1')                 // Before Roll No
    .replace(/(Your candidate was absent today\.)/i, '<br>$1')
    .replace(/(Contact\s*\d{10})/i, '<br>$1')                 // Before Contact number
    .replace(/\s{2,}/g, ' ');                                 // Clean extra spaces

  // 4️⃣ Trim any leading/trailing spaces or <br>
  text = text.replace(/^(<br>)+|(<br>)+$/g, '').trim();

  return text;
}

export default function InboxTab({ fromDate, toDate }) {
  const [messages, setMessages] = useState([]);
  const [dairy, setDairy] = useState(null);
  const [loading, setLoading] = useState(false);
  const API_BASE = process.env.REACT_APP_API_URL;
  const farmerUsername = localStorage.getItem("farmerUsername") || "F-B-3";
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]); // Scroll when messages update

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/getTransactions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: farmerUsername,
            from_date: fromDate,
            to_date: toDate,
            trantype: "null",      // 🔹 always send trantype as "null"
            group_by_type: false,
          }),
        });

        if (!res.ok) throw new Error("Failed to fetch");

        const data = await res.json();
        console.log("Fetched transactions:", data);
        setDairy(data.dairy || null);
        setMessages(data.transactions || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [fromDate, toDate, farmerUsername]);

  // 🔹 Format date to DD/MM/YYYY
  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB"); // 01/08/2025
    } catch {
      return dateStr;
    }
  };

  const fmt = (val, digits = 2) => {
    const num = Number(val);
    return isNaN(num) ? "0.00" : num.toFixed(digits);
  };

  const renderMessage = (msg, index) => {
    const dairyName = dairy?.companyname || "डेअरी";

    if (msg.trantype === "COLLECTION") {
      const code = String(msg.milktypecode).trim();
      const milkType = code === "1" ? "गाय" : code === "2" ? "म्हैस" : "-";
      console.log("दुधाचा प्रकार:", milkType);

      const sft_code = String(msg.shift_code).trim();
      const collshift = sft_code === "1" ? "सकाळ" : sft_code === "2" ? "सायं" : "-";
      return (
        <Box key={index} sx={{width:'fit-content', mb: 1, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
          <Typography fontWeight="bold">{dairyName}</Typography>
          <Typography>दि. {formatDate(msg.tran_date)} {collshift}</Typography>
          <Typography>{milkType} लिटर्स : {fmt(msg.qty, 3)}</Typography>
          <Typography>Fat : {fmt(msg.fat, 1)} Snf : {fmt(msg.snf, 1)}</Typography>
          <Typography>
            दर रु. {fmt(msg.rate)} रु. {fmt(msg.baseamt)}
          </Typography>
        </Box>
      );
    }
    if (msg.trantype === "AUS-GVN" || msg.trantype === "AUS-NAVE") {
      return (
        <Box key={index} sx={{width:'fit-content', mb: 1, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
          <Typography fontWeight="bold">{dairyName}</Typography>
          <Typography>
            दि. {formatDate(msg.tran_date)} रोजी रु.{fmt(msg.dramt, 0)} तुमचे औषध खात्यावर नावे करण्यात आले आहेत.
          </Typography>
          <Typography>
            {(msg.balance || "").replace(/AUS.*$/, "").trim()}
          </Typography>
        </Box>
      );
    }

    if (msg.trantype === "AIC-GVN" || msg.trantype === "AIC-NAVE") {
      return (
        <Box key={index} sx={{width:'fit-content', mb: 1, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
          <Typography fontWeight="bold">{dairyName}</Typography>
          <Typography>
            दि. {formatDate(msg.tran_date)} रोजी रु.{fmt(msg.dramt, 0)} तुमचे AI चार्जेस खात्यावर नावे करण्यात आले आहेत.
          </Typography>
          <Typography>
            {(msg.balance || "").replace(/AIC.*$/, "").trim()}
          </Typography>
        </Box>
      );
    }
    if (msg.trantype === "ADV-NAVE" ||msg.trantype === "ADV-GVN") {
      return (
        <Box key={index} sx={{width:'fit-content', mb: 1, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
          <Typography fontWeight="bold">{dairyName}</Typography>
          <Typography>
            दि. {formatDate(msg.tran_date)} रोजी रु.{fmt(msg.dramt, 0)} तुमचे अडवांस खात्यावर नावे करण्यात आले आहेत.
          </Typography>
          <Typography>
            {(msg.balance || "").replace(/ADV.*$/, "").trim()}
          </Typography>
        </Box>
      );
    }

    if (msg.trantype === "ADV-JAMA" || msg.trantype === "ADV-RCD"){
      return (
        <Box key={index} sx={{width:'fit-content', mb: 1, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
          <Typography fontWeight="bold">{dairyName}</Typography>
          <Typography>
            दि. {formatDate(msg.tran_date)} रोजी रु.{fmt(msg.cramt, 0)} तुमचे अडवांस खात्यावर जमा करण्यात आले आहेत.
          </Typography>
          <Typography>
            {(msg.balance || "").replace(/ADV.*$/, "").trim()}
          </Typography>
        </Box>
      );
    }

    if (msg.trantype === "CFD-GVN" || msg.trantype === "CFD-NAVE") {
      return (
        <Box key={index} sx={{width:'fit-content', mb: 1, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
          <Typography fontWeight="bold">{dairyName}</Typography>
          <Typography>
            दि. {formatDate(msg.tran_date)} रोजी रु.{fmt(msg.dramt, 0)} तुमचे खाद्य खात्यावर नावे करण्यात आले आहेत.
          </Typography>
          <Typography>
            {(msg.balance || "").replace(/CFD.*$/, "").trim()}
          </Typography>
        </Box>
      );
    }

    if (msg.trantype === "CFD-RCD" || msg.trantype === "CFD-JAMA") {
      return (
        <Box key={index} sx={{width:'fit-content', mb: 1, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
          <Typography fontWeight="bold">{dairyName}</Typography>
          <Typography>
            दि. {formatDate(msg.tran_date)} रोजी रु.{fmt(msg.cramt, 0)} तुमचे खाद्य खात्यावर जमा करण्यात आले आहेत.
          </Typography>
          <Typography>
            {(msg.balance || "").replace(/CFD.*$/, "").trim()}
          </Typography>
        </Box>
      );
    }

    if (msg.trantype === "DUD-GVN" || msg.trantype === "DUD-NAVE") {
      return (
        <Box key={index} sx={{width:'fit-content', mb: 1, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
          <Typography fontWeight="bold">{dairyName}</Typography>
          <Typography>
            दि. {formatDate(msg.tran_date)} रोजी रु.{fmt(msg.dramt, 0)} तुमचे दूध विक्री खात्यावर नावे करण्यात आले आहेत.
          </Typography>
          <Typography>
            {(msg.balance || "").replace(/DUD.*$/, "").trim()}
          </Typography>
        </Box>
      );
    }

    if (msg.trantype === "DUD-RCD" || msg.trantype === "DUD-JAMA") {
      return (
        <Box key={index} sx={{width:'fit-content', mb: 1, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
          <Typography fontWeight="bold">{dairyName}</Typography>
          <Typography>
            दि. {formatDate(msg.tran_date)} रोजी रु.{fmt(msg.cramt, 0)} तुमचे दूध विक्री खात्यावर जमा करण्यात आले आहेत.
          </Typography>
          <Typography>
            {(msg.balance || "").replace(/DUD.*$/, "").trim()}
          </Typography>
        </Box>
      );
    }

    //Just to check general college message
    if (msg.trantype === "COLG-BULK-SMS") {
      // const safeHTML = DOMPurify.sanitize(msg.remark);
      const formattedRemark = formatRemark(msg.remark);
      const safeHTML = DOMPurify.sanitize(formattedRemark);
      return (
        <Box key={index} sx={{width:'fit-content', mb: 1, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
          <Typography dangerouslySetInnerHTML={{ __html: safeHTML }} />
        </Box>
      );
    }

    

    if (msg.trantype && msg.trantype.toUpperCase().includes("SMS")) {
      const safeHTML = DOMPurify.sanitize(msg.remark);
      return (
        <Typography
          variant="body2"
          component="div"
          dangerouslySetInnerHTML={{ __html: safeHTML }}
        />
      );
    }    
    // fallback
    return (
      <Box key={index} sx={{ mb: 2, p: 2, bgcolor: "#fff", borderRadius: 2, boxShadow: 1 }}>
        <Typography fontWeight="bold">{dairyName}</Typography>
        <Typography>
          दि. {formatDate(msg.tran_date)} - {msg.trantype} ₹{fmt(msg.amount)}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ maxHeight: "70vh", overflowY: "auto", pr: 1, bgcolor: "#ece5dd" }}>
      {loading ? (
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : messages.length ? (
        <>
          {messages.map(renderMessage)}
          <div ref={messagesEndRef} />
        </>
      ) : (
        <Typography
          variant="body1"
          sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}
        >
          {/* कोणतेही रेकॉर्ड आढळले नाहीत */}
        </Typography>
      )}
    </Box>
  );
}